import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  Building2,
  Plus,
  ChevronRight,
  Pencil,
  Trash2,
  AlertTriangle,
  X,
} from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type BrandSummary = {
  id: string;
  name: string;
  asinCount: number;
  flaggedThisMonth: number;
  takedownsSubmitted: number;
  removed: number;
};

export default function DashboardBrands() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx: _ctx }: { ctx: AppShellContext }) {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<BrandSummary[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [brandToDelete, setBrandToDelete] = useState<BrandSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ brands: BrandSummary[] }>("/customer/brands").then((res) => {
      if (cancelled) return;
      setBrands(res.data?.brands ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Brands
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Every brand under your account.
          </p>
        </div>
        <Link
          to="/dashboard/brands/new"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          <Plus className="h-4 w-4" /> Add brand
        </Link>
      </div>

      {brands === null && (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading brands…
        </div>
      )}

      {brands && brands.length === 0 && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-900">
          <Building2 className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            No brands yet. Add your first to start monitoring.
          </p>
          <Link
            to="/dashboard/brands/new"
            className="mt-4 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            + Add a brand
          </Link>
        </div>
      )}

      {brands && brands.length > 0 && (
        <ul className="mt-6 space-y-3">
          {brands.map((b) => (
            <BrandCard
              key={b.id}
              brand={b}
              expanded={expanded === b.id}
              onToggle={() => setExpanded((cur) => (cur === b.id ? null : b.id))}
              onEdit={() => navigate(`/dashboard/brands/${b.id}/edit`)}
              onDelete={() => setBrandToDelete(b)}
            />
          ))}
        </ul>
      )}

      {brandToDelete && (
        <DeleteBrandModal
          brand={brandToDelete}
          onCancel={() => setBrandToDelete(null)}
          onDeleted={() => {
            setBrandToDelete(null);
            setReload((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

type BrandReview = {
  id: string;
  asin: string;
  productTitle: string | null;
  rating: number;
  title: string | null;
  body: string;
  createdAt: string;
  classification: {
    actionStatus: string;
    rationale: string | null;
  } | null;
};

function BrandCard({
  brand,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  brand: BrandSummary;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [reviews, setReviews] = useState<BrandReview[] | null>(null);

  useEffect(() => {
    if (!expanded) return;
    if (reviews !== null) return;
    let cancelled = false;
    api<{ reviews: BrandReview[] }>(`/customer/brands/${brand.id}/reviews`).then(
      (res) => {
        if (cancelled) return;
        setReviews(res.data?.reviews ?? []);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [expanded, brand.id, reviews]);

  return (
    <li className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-4 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60"
        >
          <Building2 className="h-5 w-5 shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-3">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {brand.name}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {brand.asinCount} product{brand.asinCount === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs">
            <Stat label="Flagged" value={brand.flaggedThisMonth} tone="amber" />
            <Stat label="Submitted" value={brand.takedownsSubmitted} tone="sky" />
            <Stat label="Removed" value={brand.removed} tone="emerald" />
            <ChevronRight
              className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </div>
        </button>
        <div className="flex items-center gap-0.5 pr-3">
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit ${brand.name}`}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Delete ${brand.name}`}
            className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/dashboard/reviews?brand=${brand.id}`}
              className="text-sm font-medium text-slate-900 hover:underline dark:text-slate-100"
            >
              View flagged reviews →
            </Link>
            <Link
              to={`/dashboard/products?brand=${brand.id}`}
              className="text-sm text-slate-600 hover:underline dark:text-slate-400"
            >
              View products
            </Link>
          </div>
          <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Recently flagged
          </div>
          {reviews === null && (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {reviews && reviews.length === 0 && (
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Nothing flagged yet for this brand.
            </p>
          )}
          {reviews && reviews.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {reviews.slice(0, 5).map((r) => (
                <li key={r.id} className="text-sm text-slate-700 dark:text-slate-300">
                  <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {r.asin}
                  </span>{" "}
                  · {r.title ? `"${r.title}" — ` : ""}
                  {r.body.slice(0, 100)}
                  {r.body.length > 100 ? "…" : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function DeleteBrandModal({
  brand,
  onCancel,
  onDeleted,
}: {
  brand: BrandSummary;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setError(null);
    setSubmitting(true);
    const res = await api(`/customer/brands/${brand.id}`, { method: "DELETE" });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error ?? "Couldn't delete the brand.");
      return;
    }
    onDeleted();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Delete {brand.name}?
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          This removes the brand and its {brand.asinCount} monitored product
          {brand.asinCount === 1 ? "" : "s"}. Reviews and takedown history are
          preserved as audit records.
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This can&apos;t be undone.
        </p>
        {error && (
          <p className="mt-4 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Deleting…" : "Delete brand"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "amber" | "sky" | "emerald";
}) {
  const cls =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "sky"
        ? "text-sky-700 dark:text-sky-300"
        : "text-amber-700 dark:text-amber-300";
  return (
    <span className="hidden text-right sm:inline-flex sm:flex-col sm:items-end">
      <span className={`text-sm font-semibold ${cls}`}>{value}</span>
      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </span>
  );
}
